import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { captureException } from '@sentry/core';
import DataLoader from 'dataloader';
import { proxy } from 'valtio';
import { type Awareness } from 'y-protocols/awareness';

import type { UserProfileIdentity } from '@canvas/atlassian-client/identity/types';
import type { BackgroundColorToken } from '@canvas/constants/colors/tokens-types';
import { getTelepointerColorTokenForCollaborator } from '@canvas/constants/get-color-value';
import { type AwarenessClientIDs } from '@canvas/model/awareness';
import {
  AGENT_ID,
  AI_AGENT_USER,
  type CanvasIdentity,
  type Identity,
  IdentityKind
} from '@canvas/model/identity';
import { type ClientId } from '@canvas/model/telepointers';

const COLORS_TO_OMIT = new Set<BackgroundColorToken>([
  'color.background.accent.blue.bolder'
]);

export interface Participant {
  identity: Identity;
  colorToken: BackgroundColorToken;
}

const createParticipantFromIdentity = (
  identity: Identity,
  clientId: number
): Participant => {
  return {
    identity,
    colorToken: getTelepointerColorTokenForCollaborator(clientId, COLORS_TO_OMIT)
  };
};

type FieldEntryGrouping =
  | { fieldId: string; entryId: string }
  | { fieldId: string }
  | { entryId: string };

export interface PresenceStateApi {
  addParticipant: (grouping: FieldEntryGrouping, participant: Participant) => void;
  removeParticipant: (participant: Participant) => void;
}

export const awarenessFields = {
  telepointers: 't2',
  identity: 'i',
  aiFieldRun: 'air',
  aiFieldCellRequests: 'aicr'
} as const;

interface AwarenessState {
  t2: [string, string];
  i: string;
  air?: unknown;
  aicr?: unknown;
}

export type DatabaseAiFieldRunAwareness = {
  protocolVersion: 1;
  runId: string;
  aiFieldType: string;
  fieldId: string;
  phase: 'validating' | 'generating';
  scope:
    | { kind: 'entry'; entryId: string }
    | { kind: 'entries'; entryIds: readonly string[] }
    | {
        kind: 'field';
        operation: 'all-entries' | 'empty-target-entries';
      };
};

export type DatabaseAiFieldCellRequestAwareness = {
  protocolVersion: 1;
  requests: ReadonlyArray<{
    aiFieldType: string;
    fieldId: string;
    entryId: string;
    status: 'queued' | 'running';
  }>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const parseDatabaseAiFieldRunAwareness = (
  value: unknown
): DatabaseAiFieldRunAwareness | undefined => {
  if (
    !isRecord(value) ||
    value.protocolVersion !== 1 ||
    typeof value.runId !== 'string' ||
    value.runId === '' ||
    typeof value.aiFieldType !== 'string' ||
    value.aiFieldType.trim() === '' ||
    typeof value.fieldId !== 'string' ||
    value.fieldId === '' ||
    (value.phase !== 'validating' && value.phase !== 'generating') ||
    !isRecord(value.scope)
  ) {
    return undefined;
  }

  const scope = value.scope;
  if (
    scope.kind === 'entry' &&
    typeof scope.entryId === 'string' &&
    scope.entryId !== ''
  ) {
    return { ...value, scope } as DatabaseAiFieldRunAwareness;
  }

  if (
    scope.kind === 'entries' &&
    Array.isArray(scope.entryIds) &&
    scope.entryIds.length > 0 &&
    scope.entryIds.every((entryId) => typeof entryId === 'string' && entryId !== '')
  ) {
    return { ...value, scope } as DatabaseAiFieldRunAwareness;
  }

  if (
    scope.kind === 'field' &&
    (scope.operation === 'all-entries' || scope.operation === 'empty-target-entries')
  ) {
    return { ...value, scope } as DatabaseAiFieldRunAwareness;
  }

  return undefined;
};

export const parseDatabaseAiFieldCellRequestAwareness = (
  value: unknown
): DatabaseAiFieldCellRequestAwareness | undefined => {
  if (!isRecord(value) || value.protocolVersion !== 1 || !Array.isArray(value.requests)) {
    return undefined;
  }

  const requests = value.requests;
  if (
    requests.some(
      (request) =>
        !isRecord(request) ||
        typeof request.aiFieldType !== 'string' ||
        request.aiFieldType.trim() === '' ||
        typeof request.fieldId !== 'string' ||
        request.fieldId === '' ||
        typeof request.entryId !== 'string' ||
        request.entryId === '' ||
        (request.status !== 'queued' && request.status !== 'running')
    )
  ) {
    return undefined;
  }

  return { protocolVersion: 1, requests } as DatabaseAiFieldCellRequestAwareness;
};

export type TelepointerState = {
  identity: Identity;
  entryId: string;
  fieldId: string;
};

interface YDatabaseTelepointersOptions {
  telepointerAwareness: Awareness;
  participantAwareness?: Awareness;
  identitySupplier: (() => Promise<Identity>) | null;
  userSupplier:
    | ((accountIds: readonly string[]) => Promise<(UserProfileIdentity | undefined)[]>)
    | null;
  presenceState?: PresenceStateApi | null;
}

export class YDatabaseTelepointers {
  telepointers: Record<number, TelepointerState> = proxy({});
  participants: Record<number, Identity> = proxy({});
  aiFieldRuns: Record<number, DatabaseAiFieldRunAwareness> = proxy({});
  aiFieldCellRequests: Record<number, DatabaseAiFieldCellRequestAwareness> = proxy({});
  fullIdentityProfiles: DataLoader<string, UserProfileIdentity | undefined> | null;
  #localIdentity: Identity | null = null;
  #presenceState: PresenceStateApi | null;
  readonly awareness: Awareness;
  readonly #participantAwareness?: Awareness;
  readonly #participantAwarenessUpdateHandler: (
    clientIds: AwarenessClientIDs,
    origin: string
  ) => void;

  /**
   * @param options Awareness channels and identity dependencies used by database presence.
   */
  constructor({
    telepointerAwareness,
    participantAwareness,
    identitySupplier,
    userSupplier,
    presenceState = null
  }: YDatabaseTelepointersOptions) {
    this.awareness = telepointerAwareness;
    this.#participantAwareness = participantAwareness;
    this.#participantAwarenessUpdateHandler = participantAwareness
      ? (clientIds, origin) =>
          this.onParticipantAwarenessUpdate(participantAwareness, clientIds, origin)
      : () => {};
    this.#presenceState = presenceState;
    this.fullIdentityProfiles = userSupplier
      ? new DataLoader(userSupplier, { maxBatchSize: 90 })
      : null;
    this.awareness.on('change', this.onAwarenessUpdate);
    this.#participantAwareness?.on('change', this.#participantAwarenessUpdateHandler);

    identitySupplier?.().then((identity) => {
      setAccountId(identity);
      this.#localIdentity = identity;
      this.awareness.setLocalStateField(awarenessFields.identity, identity.accountId);
    });

    this.loadExistingAwarenessStates();
    this.loadExistingParticipantAwarenessStates();
  }

  setCursorPosition(entryId?: string, fieldId?: string) {
    this.awareness.setLocalStateField(awarenessFields.telepointers, [entryId, fieldId]);
  }

  setAiFieldRun(run: DatabaseAiFieldRunAwareness | undefined): void {
    this.awareness.setLocalStateField(awarenessFields.aiFieldRun, run ?? null);
    if (run === undefined) {
      delete this.aiFieldRuns[this.awareness.clientID];
    } else {
      this.aiFieldRuns[this.awareness.clientID] = run;
    }
  }

  setAiFieldCellRequests(
    requests: DatabaseAiFieldCellRequestAwareness | undefined
  ): void {
    this.awareness.setLocalStateField(
      awarenessFields.aiFieldCellRequests,
      requests ?? null
    );
    if (requests === undefined) {
      delete this.aiFieldCellRequests[this.awareness.clientID];
    } else {
      this.aiFieldCellRequests[this.awareness.clientID] = requests;
    }
  }

  clearAiFieldActivity(): void {
    this.awareness.setLocalStateField(awarenessFields.aiFieldRun, null);
    this.awareness.setLocalStateField(awarenessFields.aiFieldCellRequests, null);

    for (const clientId of Object.keys(this.aiFieldRuns)) {
      delete this.aiFieldRuns[Number(clientId)];
    }
    for (const clientId of Object.keys(this.aiFieldCellRequests)) {
      delete this.aiFieldCellRequests[Number(clientId)];
    }
  }

  clearRemoteAiFieldActivity(): void {
    const localClientId = this.awareness.clientID;
    for (const clientId of Object.keys(this.aiFieldRuns)) {
      if (Number(clientId) !== localClientId) {
        delete this.aiFieldRuns[Number(clientId)];
      }
    }
    for (const clientId of Object.keys(this.aiFieldCellRequests)) {
      if (Number(clientId) !== localClientId) {
        delete this.aiFieldCellRequests[Number(clientId)];
      }
    }
  }

  dispose() {
    // Clear DataLoader cache
    this.fullIdentityProfiles?.clearAll();
    this.fullIdentityProfiles = null;

    // Clear proxy objects
    for (const key in this.telepointers) {
      delete this.telepointers[Number(key)];
    }
    for (const key in this.participants) {
      delete this.participants[Number(key)];
    }
    for (const key in this.aiFieldRuns) {
      delete this.aiFieldRuns[Number(key)];
    }
    for (const key in this.aiFieldCellRequests) {
      delete this.aiFieldCellRequests[Number(key)];
    }

    this.awareness.off('change', this.onAwarenessUpdate);
    this.#participantAwareness?.off('change', this.#participantAwarenessUpdateHandler);
    this.awareness.destroy();
    this.#participantAwareness?.destroy();
  }

  /**
   * Both field and entry id are optional. If one is provided and the other omitted, it will be assumed the whole field/entry is selected.
   * @param clientId
   * @param identity
   * @param entryId
   * @param fieldId
   */
  public updatePresenceState(
    clientId: number,
    identity: Identity,
    fieldId: string,
    entryId: string
  ) {
    const isLocal = process.env.NODE_ENV === 'development';
    if (
      !isLocal &&
      this.#localIdentity &&
      this.isSameAccount(identity, this.#localIdentity)
    ) {
      return;
    }

    this.#presenceState?.addParticipant(
      { fieldId, entryId },
      createParticipantFromIdentity(identity, clientId)
    );
  }

  private extractAccountIdFromAriFormat(ariOrAccountId: string): string {
    if (IdentityUserAri.check(ariOrAccountId)) {
      return IdentityUserAri.parse(ariOrAccountId).resourceId;
    }
    return ariOrAccountId;
  }

  private isSameAccount(identity1: Identity, identity2: Identity): boolean {
    return (
      this.extractAccountIdFromAriFormat(identity1.accountId) ===
      this.extractAccountIdFromAriFormat(identity2.accountId)
    );
  }

  private loadExistingAwarenessStates() {
    const states = this.awareness.getStates();
    const existingClientIds = Array.from(states.keys()).filter(
      (clientId) => clientId !== this.awareness.clientID
    );

    if (existingClientIds.length > 0) {
      this.onAwarenessUpdate(
        { added: existingClientIds, updated: [], removed: [] },
        'initialization'
      );
    }
  }

  private loadExistingParticipantAwarenessStates() {
    const participantAwareness = this.#participantAwareness;

    if (!participantAwareness) {
      return;
    }

    const states = participantAwareness.getStates();
    const existingClientIds = Array.from(states.keys()).filter(
      (clientId) => clientId !== participantAwareness.clientID
    );

    if (existingClientIds.length > 0) {
      this.onParticipantAwarenessUpdate(
        participantAwareness,
        { added: existingClientIds, updated: [], removed: [] },
        'initialization'
      );
    }
  }

  private onAwarenessUpdate = (
    {
      added: addedClientIds,
      updated: updatedClientIds,
      removed: deletedClientIds
    }: AwarenessClientIDs,
    origin: string
  ) => {
    const states = this.awareness.getStates();
    for (const clientId of [...addedClientIds, ...updatedClientIds]) {
      if (origin === 'local' && clientId === this.awareness.clientID) {
        continue;
      }

      const run = parseDatabaseAiFieldRunAwareness(
        states.get(clientId)?.[awarenessFields.aiFieldRun]
      );
      if (run === undefined) {
        delete this.aiFieldRuns[clientId];
      } else {
        this.aiFieldRuns[clientId] = run;
      }

      const requests = parseDatabaseAiFieldCellRequestAwareness(
        states.get(clientId)?.[awarenessFields.aiFieldCellRequests]
      );
      if (requests === undefined) {
        delete this.aiFieldCellRequests[clientId];
      } else {
        this.aiFieldCellRequests[clientId] = requests;
      }
    }
    for (const clientId of deletedClientIds) {
      delete this.aiFieldRuns[clientId];
      delete this.aiFieldCellRequests[clientId];
    }

    if (origin !== 'local') {
      for (const clientId of [...addedClientIds, ...updatedClientIds]) {
        if (clientId === this.awareness.clientID) {
          continue;
        }

        const accountId =
          typeof states.get(clientId)?.[awarenessFields.identity] === 'string'
            ? states.get(clientId)?.[awarenessFields.identity]
            : // For backwards compatibility with older clients
              states.get(clientId)?.[awarenessFields.identity]?.accountId;

        if (!accountId) {
          continue;
        }

        // @TODO: Make telepointers support anonymous users by not attempting to fetch or construct
        // the partial identity for anonymous users, but instead just set the participant and telepointer
        // with the Anonymous Identity type
        this.fullIdentityProfiles
          ?.load(accountId)
          .then((identityProfile) => {
            // We need to check state again, in case it has since been deleted
            if (identityProfile) {
              // If we were able to load the full profile, we update the participant and
              // telepointer with their full identity
              const identity = this.getIdentityFromProfile(identityProfile);
              this.setParticipantsAndTelepointers(clientId, identity);
            }
          })
          .catch((e) => captureException(e));
      }

      // Do a single batch update after the loop
      const participantsToRemove = [];
      for (const clientId of deletedClientIds) {
        const identity = this.participants[clientId];
        if (identity) {
          const participant = createParticipantFromIdentity(identity, clientId);
          participantsToRemove.push(participant);
        }
        delete this.telepointers[clientId];
        delete this.participants[clientId];
      }

      if (participantsToRemove.length > 0 && this.#presenceState) {
        const presenceState = this.#presenceState;
        participantsToRemove.forEach((participant) => {
          presenceState.removeParticipant(participant);
        });
      }
    }
  };

  private onParticipantAwarenessUpdate = (
    participantAwareness: Awareness,
    {
      added: addedClientIds,
      updated: updatedClientIds,
      removed: deletedClientIds
    }: AwarenessClientIDs,
    origin: string
  ) => {
    if (origin === 'local') {
      return;
    }

    const states = participantAwareness.getStates();
    for (const clientId of [...addedClientIds, ...updatedClientIds]) {
      if (clientId === participantAwareness.clientID) {
        continue;
      }

      const identityState = states.get(clientId)?.[awarenessFields.identity];
      const accountId =
        typeof identityState === 'string'
          ? identityState
          : isRecord(identityState) && typeof identityState.accountId === 'string'
            ? identityState.accountId
            : undefined;

      if (accountId === AGENT_ID) {
        this.participants[clientId] = AI_AGENT_USER;
      } else if (this.participants[clientId]?.type === IdentityKind.AiAgent) {
        delete this.participants[clientId];
      }
    }

    for (const clientId of deletedClientIds) {
      if (this.participants[clientId]?.type === IdentityKind.AiAgent) {
        delete this.participants[clientId];
      }
    }
  };

  private getIdentityFromProfile(identityProfile: UserProfileIdentity): CanvasIdentity {
    return {
      accountId: identityProfile.id,
      displayName: identityProfile.name,
      nickname: identityProfile.nickname ?? identityProfile.name,
      picture: identityProfile.picture,
      email: identityProfile.email,
      locale: identityProfile.locale,
      type: IdentityKind.CanvasId
    } satisfies CanvasIdentity;
  }

  /**
   * Given a clientId and an identity, sets the participant and telepointer state
   * @param clientId The clientId of the participant
   * @param identity The identity of the participant
   */
  private setParticipantsAndTelepointers(clientId: ClientId, identity: Identity) {
    const state = this.awareness.getStates().get(clientId) as AwarenessState;

    const identityState = state?.[awarenessFields.identity];
    if (identityState) {
      this.participants[clientId] = identity;
    }

    const telepointers = state?.[awarenessFields.telepointers];
    if (Array.isArray(telepointers)) {
      const [entryId, fieldId] = telepointers;
      this.updatePresenceState(clientId, identity, fieldId, entryId);
      this.telepointers[clientId] = {
        entryId,
        fieldId,
        identity
      };
    }
  }
}

// For backwards compat with old clients
function setAccountId(identity: Identity | undefined) {
  if (identity && 'account_id' in identity) {
    identity.accountId = identity.account_id as string;
  }
}
